import React, { useEffect, useState } from 'react';
import { apiFetch } from '../api';

interface PlanFactRow {
  articleId: number;
  productId: number | null;
  code: string;
  name: string;
  planSum: number;
  factSum: number;
  delta: number;
}

interface ForecastRow {
  articleId: number;
  forecastDynamic: number;
}

interface Product {
  id: number;
  name: string;
}

interface ArticleInfo {
  id: number;
  is_income: boolean;
  parentId: number | null;
  group_number: string;
  number: string;
  code: string;
  name?: string;
}

interface ProductEntry {
  productId: number;
  name: string;
  plan: number;
  fact: number;
  forecast: number;
  deviation: number;
}

interface ArticleEntry {
  articleId: number;
  code: string;
  name: string;
  isIncome: boolean;
  parentId: number | null;
  groupNumber: number;
  number: number;
  plan: number;
  fact: number;
  forecast: number;
  deviation: number;
  products: ProductEntry[];
  children: ArticleEntry[];
}

function formatNumber(num: number) {
  const hasFraction = Math.abs(num % 1) > 0;
  const fractionDigits = hasFraction ? 2 : 0;
  return num.toLocaleString('ru-RU', {
    minimumFractionDigits: fractionDigits,
    maximumFractionDigits: fractionDigits,
  });
}

const PlanFactTable: React.FC = () => {
  const [incomeData, setIncomeData] = useState<ArticleEntry[]>([]);
  const [expenseData, setExpenseData] = useState<ArticleEntry[]>([]);
  const [expandedCats, setExpandedCats] = useState<{ income: boolean; expense: boolean }>({ income: false, expense: false });
  const [expandedArticles, setExpandedArticles] = useState<Set<number>>(new Set());
  const [allExpanded, setAllExpanded] = useState(false);
  const [dateContext] = useState(() => {
    const now = new Date();
    if (now.getDate() === 1) {
      now.setDate(0); // move to last day of previous month
    }
    const daysInMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0).getDate();
    return { dayOfMonth: now.getDate(), daysInMonth, month: now.toISOString().slice(0, 7) };
  });

  useEffect(() => {
    async function load() {
      const { dayOfMonth, daysInMonth, month } = dateContext;
      const plannedToDate = (plan: number) => (daysInMonth ? (plan / daysInMonth) * dayOfMonth : 0);
      const [pfRes, fcRes, prodRes, artRes] = await Promise.all([
        apiFetch(`/api/dashboard/plan-fact?month=${month}`),
        apiFetch(`/api/dashboard/forecast?month=${month}`),
        apiFetch('/api/products'),
        apiFetch('/api/articles'),
      ]);
      const pfData: PlanFactRow[] = await pfRes.json();
      const fcData: ForecastRow[] = await fcRes.json();
      const products: Product[] = await prodRes.json();
      const articles: ArticleInfo[] = await artRes.json();
      const fcMap = new Map(fcData.map(r => [r.articleId, r.forecastDynamic]));
      const prodMap = new Map(products.map(p => [p.id, p.name]));
      const artMap = new Map(articles.map(a => [a.id, a]));
      const grouped = new Map<number, ArticleEntry>();

      function rootIncome(id: number): boolean {
        let cur = artMap.get(id);
        let last = cur;
        while (cur && cur.parentId) {
          last = artMap.get(cur.parentId);
          cur = last;
        }
        return last ? !!last.is_income : false;
      }

      function ensureArticle(id: number): ArticleEntry {
        const info = artMap.get(id);
        const existing = grouped.get(id);
        if (existing) return existing;
        const art: ArticleEntry = {
          articleId: id,
          code: info?.code || '',
          name: info?.name || '',
          isIncome: rootIncome(id),
          parentId: info?.parentId ?? null,
          groupNumber: Number(info?.group_number ?? 0),
          number: Number(info?.number ?? 0),
          plan: 0,
          fact: 0,
          forecast: 0,
          deviation: 0,
          products: [],
          children: [],
        };
        grouped.set(id, art);
        if (art.parentId) {
          const parent = ensureArticle(art.parentId);
          if (!parent.children.find(c => c.articleId === art.articleId)) {
            parent.children.push(art);
          }
        }
        return art;
      }

      const forecastFromFact = (factValue: number) =>
        dateContext.dayOfMonth
          ? (factValue / dateContext.dayOfMonth) * dateContext.daysInMonth
          : 0;

      pfData.forEach(row => {
        const info = artMap.get(row.articleId);
        const art = ensureArticle(row.articleId);
        const plan = Math.abs(Number(row.planSum || 0));
        const fact = Math.abs(Number(row.factSum || 0));
        art.plan += plan;
        art.fact += fact;
        if (row.productId) {
          art.products.push({
            productId: row.productId,
            name: prodMap.get(row.productId) || `ID ${row.productId}`,
            plan,
            fact,
            forecast: forecastFromFact(fact),
            deviation: fact - plannedToDate(plan),
          });
        }
        let pid = info?.parentId ?? null;
        while (pid) {
          const par = ensureArticle(pid);
          par.plan += plan;
          par.fact += fact;
          pid = artMap.get(pid)?.parentId ?? null;
        }
      });
      grouped.forEach((art, id) => {
        art.forecast = forecastFromFact(art.fact);
        art.deviation = art.fact - plannedToDate(art.plan);
      });

      const sortTree = (items: ArticleEntry[]) => {
        items.sort((a, b) => {
          if (a.groupNumber !== b.groupNumber) return a.groupNumber - b.groupNumber;
          return a.number - b.number;
        });
        items.forEach(it => sortTree(it.children));
      };

      const rootsIncome: ArticleEntry[] = [];
      const rootsExpense: ArticleEntry[] = [];
      grouped.forEach(art => {
        if (!art.parentId) {
          (art.isIncome ? rootsIncome : rootsExpense).push(art);
        }
      });
      sortTree(rootsIncome);
      sortTree(rootsExpense);

      setIncomeData(rootsIncome);
      setExpenseData(rootsExpense);
    }
    load().catch(console.error);
  }, []);

  function toggleCategory(cat: 'income' | 'expense') {
    setExpandedCats(c => ({ ...c, [cat]: !c[cat] }));
  }

  function toggleArticle(id: number) {
    setExpandedArticles(s => {
      const n = new Set(s);
      n.has(id) ? n.delete(id) : n.add(id);
      return n;
    });
  }

  function toggleAll() {
    if (allExpanded) {
      setExpandedCats({ income: false, expense: false });
      setExpandedArticles(new Set());
    } else {
      setExpandedCats({ income: true, expense: true });
      const ids: number[] = [];
      const collectIds = (items: ArticleEntry[]) => {
        items.forEach(it => {
          ids.push(it.articleId);
          if (it.children.length) collectIds(it.children);
        });
      };
      collectIds(incomeData);
      collectIds(expenseData);
      setExpandedArticles(new Set(ids));
    }
    setAllExpanded(!allExpanded);
  }

  const income = incomeData;
  const expense = expenseData;

  const totalPlanIncome = income.reduce((s, d) => s + d.plan, 0);
  const totalPlanExpense = expense.reduce((s, d) => s + d.plan, 0);
  const totalFactIncome = income.reduce((s, d) => s + d.fact, 0);
  const totalFactExpense = expense.reduce((s, d) => s + d.fact, 0);
  const totalForecastIncome = income.reduce((s, d) => s + d.forecast, 0);
  const totalForecastExpense = expense.reduce((s, d) => s + d.forecast, 0);
  const totalDeviationIncome = income.reduce((s, d) => s + d.deviation, 0);
  const totalDeviationExpense = expense.reduce((s, d) => s + d.deviation, 0);

  const totalPlan = totalPlanIncome - totalPlanExpense;
  const totalFact = totalFactIncome - totalFactExpense;
  const totalForecast = totalForecastIncome - totalForecastExpense;
  const totalDeviation = totalDeviationIncome - totalDeviationExpense;
  const isCollapsed = !expandedCats.income && !expandedCats.expense;
  const firstColClass = isCollapsed
    ? 'min-w-[120px] sm:min-w-[220px]'
    : 'min-w-[220px]';
  const planColClass = isCollapsed
    ? 'min-w-[150px] sm:min-w-[130px]'
    : 'min-w-[130px]';
  const factColClass = isCollapsed
    ? 'min-w-[150px] sm:min-w-[130px]'
    : 'min-w-[130px]';

  function renderArticle(art: ArticleEntry, cat: 'income' | 'expense', level: number): React.ReactNode[] {
    const rows: React.ReactNode[] = [];
    const hasChildren = art.children.length > 0 || art.products.length > 0;
    rows.push(
      <tr
        key={art.articleId}
        className={`cursor-pointer hover:bg-gray-100 ${expandedCats[cat] ? '' : 'hidden'}`}
        onClick={() => hasChildren && toggleArticle(art.articleId)}
      >
        <td
          className={`py-2 pl-3 sticky left-0 z-10 bg-white ${firstColClass}`}
          style={{ paddingLeft: 16 * (level + 1) + 12 }}
        >
          {hasChildren && (
            <span className="mr-1 select-none">
              {expandedArticles.has(art.articleId) ? '▼' : '▶'}
            </span>
          )}
          {`${art.code} ${art.name}`}
        </td>
        <td className="py-2 text-center">{formatNumber(art.plan)} ₽</td>
        <td className="py-2 text-center">{formatNumber(art.fact)} ₽</td>
        <td className="py-2 text-center">{formatNumber(art.forecast)} ₽</td>
        <td className="py-2 text-center">{formatNumber(art.deviation)} ₽</td>
      </tr>
    );
    if (expandedArticles.has(art.articleId)) {
      art.products.forEach(prod => {
        rows.push(
          <tr key={`p-${art.articleId}-${prod.productId}`} className={expandedCats[cat] ? '' : 'hidden'}>
            <td
              className={`py-2 pl-3 sticky left-0 z-10 bg-white ${firstColClass}`}
              style={{ paddingLeft: 16 * (level + 2) + 12 }}
            >
              {prod.name}
            </td>
            <td className="py-2 text-center">{formatNumber(prod.plan)} ₽</td>
            <td className="py-2 text-center">{formatNumber(prod.fact)} ₽</td>
            <td className="py-2 text-center">{formatNumber(prod.forecast)} ₽</td>
            <td className="py-2 text-center">{formatNumber(prod.deviation)} ₽</td>
          </tr>
        );
      });
      art.children.forEach(ch => {
        rows.push(...renderArticle(ch, cat, level + 1));
      });
    }
    return rows;
  }

  function renderGroup(title: string, items: ArticleEntry[], cat: 'income' | 'expense') {
    const bg = cat === 'income' ? 'bg-blue-50' : 'bg-red-50';
    const plan = items.reduce((s, it) => s + it.plan, 0);
    const fact = items.reduce((s, it) => s + it.fact, 0);
    const forecast = items.reduce((s, it) => s + it.forecast, 0);
    const plannedToDate = dateContext.daysInMonth ? (plan / dateContext.daysInMonth) * dateContext.dayOfMonth : 0;
    const dev = fact - plannedToDate;
    return (
      <React.Fragment>
        <tr className={`${bg} cursor-pointer`} onClick={() => toggleCategory(cat)}>
          <td className={`py-2 pl-3 font-semibold sticky left-0 z-10 ${bg} ${firstColClass}`}>{title}</td>
          <td className="py-2 text-center font-semibold">{formatNumber(plan)} ₽</td>
          <td className="py-2 text-center font-semibold">{formatNumber(fact)} ₽</td>
          <td className="py-2 text-center font-semibold">{formatNumber(forecast)} ₽</td>
          <td className="py-2 text-center font-semibold">{formatNumber(dev)} ₽</td>
        </tr>
        {items.map(art => (
          <React.Fragment key={art.articleId}>{renderArticle(art, cat, 0)}</React.Fragment>
        ))}
      </React.Fragment>
    );
  }

  return (
    <div className="max-w-7xl mx-auto bg-white rounded-2xl shadow mt-8" id="financial-block">
      <div className="flex justify-between items-center bg-white px-4 py-2 border-b rounded-t-2xl">
        <h2 className="text-2xl font-bold text-gray-800">План/факт</h2>
        <button
          className="w-10 h-10 flex items-center justify-center bg-indigo-600 text-white rounded hover:bg-indigo-700 transition focus:outline-none"
          onClick={toggleAll}
        >
          {allExpanded ? (
            <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 15l7-7 7 7" />
            </svg>
          ) : (
            <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
            </svg>
          )}
        </button>
      </div>
      <div className="overflow-auto max-h-[70vh] rounded-xl border border-gray-200">
        <table className="min-w-[720px] w-full border-collapse">
          <thead className="bg-gray-50 sticky top-0 z-20">
            <tr>
              <th className={`py-2 pl-3 text-left sticky left-0 z-30 bg-gray-50 ${firstColClass}`}></th>
              <th className={`py-2 text-center font-bold ${planColClass}`}>План</th>
              <th className={`py-2 text-center font-bold ${factColClass}`}>Факт</th>
              <th className="min-w-[130px] py-2 text-center font-bold">Прогноз</th>
              <th className="min-w-[140px] py-2 text-center font-bold">Отклонение</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {renderGroup('Приход', income, 'income')}
            {renderGroup('Расход', expense, 'expense')}
          </tbody>
          <tfoot className="bg-gray-50 border-t">
            <tr>
              <td className={`py-2 pl-3 font-bold text-lg sticky left-0 z-10 bg-gray-50 ${firstColClass}`}>Итого</td>
              <td className="py-2 text-center text-lg font-bold">{formatNumber(totalPlan)} ₽</td>
              <td className="py-2 text-center text-lg font-bold">{formatNumber(totalFact)} ₽</td>
              <td className="py-2 text-center text-lg font-bold">{formatNumber(totalForecast)} ₽</td>
              <td className="py-2 text-center text-lg font-bold">{formatNumber(totalDeviation)} ₽</td>
            </tr>
          </tfoot>
        </table>
      </div>
    </div>
  );
};

export default PlanFactTable;
